<script lang="ts">
	import { Badge } from "flowbite-svelte-v1";
	import { FormSchema, type Form } from "#gen/forms/v1/forms_pb.js";
	import Icon from "#lib/components/Icon.svelte";
	import FormAppearanceEditor from "#lib/components/flow/editor/appearance/FormAppearanceEditor.svelte";
	import type { ComponentEditingProps } from "#lib/components/page/ComponentProps.js";
	import FormContentEditor from "#lib/components/page/components/form/FormContentEditor.svelte";
	import FormDesigner from "#lib/components/page/components/form/FormDesigner.svelte";
	import FormRules from "#lib/components/page/components/form/FormRules.svelte";
	import FormSettings from "#lib/components/page/components/form/FormSettings.svelte";
	import { deepClone } from "#lib/deepClone.js";
	import FeatureFlagGate from "#lib/permissions/FeatureFlagGate.svelte";
	import { fromJson, toJson, type JsonObject } from "#lib/protobuf-compat.js";
	import { objectValue, setStructValue, structValue } from "#lib/struct.js";
	import { TabItem, Tabs } from "#lib/ui/Tabs/index.js";

	let { id, page, data, onChange }: ComponentEditingProps = $props();

	let selectedTab = $state<"content" | "appearance" | "rules">("content");

	const formData = $derived(deepClone(structValue($data, id)) as JsonObject | undefined);
	const form = $derived(fromJson(FormSchema, objectValue(formData, "form") ?? {}, { ignoreUnknownFields: true }));

	function update(form: Form) {
		if (!$data) {
			$data = {} as JsonObject;
		}
		const value = { form: toJson(FormSchema, form) };
		setStructValue($data, id, value);
		$data = $data;
		$page = $page;
	}

	function handleInput(form: Form) {
		update(form);
	}

	function handleChange(form: Form) {
		update(form);
		onChange?.(toJson(FormSchema, form));
	}
</script>

{#snippet children()}
	<FormDesigner {form} onInput={handleInput} onChange={handleChange} />
	<FormRules {form} onChange={handleChange} />
	<FormSettings {form} onInput={handleInput} onChange={handleChange} />
{/snippet}

<FeatureFlagGate flag="build_your_own_page">
	<Tabs tabStyle="underline" class="w-full px-6 pt-4" classes={{ content: "my-0 h-full min-h-0 w-full overflow-y-auto bg-transparent px-6 pt-6 pb-0" }}>
		<TabItem open={selectedTab === "content"} onclick={() => (selectedTab = "content")} data-action-name="form-editor-tab" data-action-title="Content">
			{#snippet titleSlot()}
				<div class="flex items-center gap-1.5">
					<Icon name="pencil-square" />
					Content
				</div>
			{/snippet}
			<div>
				<FormContentEditor {form} onInput={handleInput} onChange={handleChange} />
				<!-- Bottom inset so scrolled content clears the editor footer -->
				<div class="h-6 shrink-0" aria-hidden="true"></div>
			</div>
		</TabItem>
		<TabItem open={selectedTab === "appearance"} onclick={() => (selectedTab = "appearance")} data-action-name="form-editor-tab" data-action-title="Appearance">
			{#snippet titleSlot()}
				<div class="flex items-center gap-1.5">
					<Icon iconSet="material-symbols-light" name="palette-outline" />
					Appearance
				</div>
			{/snippet}
			<div>
				<FormAppearanceEditor {id} {page} {onChange} />
				<!-- Bottom inset so scrolled content clears the editor footer -->
				<div class="h-6 shrink-0" aria-hidden="true"></div>
			</div>
		</TabItem>
		<TabItem open={selectedTab === "rules"} onclick={() => (selectedTab = "rules")} data-action-name="form-editor-tab" data-action-title="Dynamic rules">
			{#snippet titleSlot()}
				<div class="flex items-center gap-1.5">
					<Icon name="adjustments-vertical" />
					Dynamic rules
					{#if form.rules.length > 0}
						<Badge color="primary" rounded class="px-2">{form.rules.length}</Badge>
					{/if}
				</div>
			{/snippet}
			<div>
				<FormRules {form} onChange={handleChange} addRuleAtBottom />
				<!-- Bottom inset so scrolled content clears the editor footer -->
				<div class="h-6 shrink-0" aria-hidden="true"></div>
			</div>
		</TabItem>
	</Tabs>
	{#snippet fallback()}
		<div class="flex flex-col px-6 pb-6">
			{@render children()}
		</div>
	{/snippet}
</FeatureFlagGate>
